/**
 * The parsers behind every route's `validateSearch`.
 *
 * TanStack Router hands `validateSearch` a `Record<string, unknown>` off the
 * query string, so each route has to narrow every field itself. The narrowing
 * rules are not per-route — "a page number", "a filter id", "a calendar day"
 * mean the same thing on all of them — but they had been written out by hand in
 * each file: `parsePage` nine times, the non-empty-string rule fourteen times
 * in two different spellings, `parseIsoDay` twice.
 *
 * What stays in the route file is the part that IS per-route: which fields
 * exist, and the domain narrowings (`parseTab`, `parseStatus`, `parseView`).
 * Those are one-line `VALUES.find(...)` lookups over a list the route already
 * owns, and naming them locally reads better than threading the list through a
 * generic helper.
 *
 * Every parser returns `undefined` for anything it doesn't recognise rather
 * than throwing or substituting a default. A search param is user-editable
 * text in a shareable URL, so junk is expected traffic, not an error — and
 * `undefined` is what lets the page apply its own default (`page ?? 1`).
 */

import {isDateOnly} from '@/lib/date-only';

/**
 * A string with actual content, or `undefined`.
 *
 * Whitespace-only counts as empty. That is a deliberate tightening of the
 * hand-written copies this replaces: most tested `Boolean(value)`, which let
 * `?q=%20%20` through, while the admin users route alone trimmed first. Every
 * position this guards is either a free-text search (which each page already
 * trims before use) or an entity id (where a blank is junk), so the stricter
 * rule is right in all of them — and one rule is what stops the two spellings
 * drifting again.
 */
export function parseNonEmpty(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * A flag that is either on or absent — never `false`.
 *
 * Accepts the string a URL actually carries as well as a real boolean, since a
 * link built by hand and one built by the router both arrive here. Anything else
 * is dropped rather than read as "off": `?mapped=maybe` should leave the filter
 * alone, not switch it on, and `undefined` is what lets a caller spread the
 * field away entirely so an off flag never appears in the URL at all.
 */
export function parseTrueFlag(value: unknown): true | undefined {
  return value === true || value === 'true' ? true : undefined;
}

/**
 * A 1-based page number, or `undefined` when absent or unusable.
 *
 * Accepts the numeric string a URL actually carries as well as a real number,
 * since a link built by hand and one built by the router both arrive here.
 * Anything below the first page is dropped rather than clamped: the page
 * defaults to 1 on `undefined`, so clamping would only add a second way of
 * saying the same thing.
 */
export function parsePage(value: unknown): number | undefined {
  if (value === undefined) return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : undefined;
}

/**
 * A well-formed `yyyy-MM-dd` calendar day, or `undefined`.
 *
 * Shape only — it shares `date-only`'s guard rather than carrying a third copy
 * of the same regex, and like that guard it does not check the day exists, so
 * `2026-02-31` passes here and the API rejects it. Tightening this to a real
 * calendar check is a behaviour change, not a refactor; it would belong in
 * `date-only` beside the guard, applying to the form fields too.
 */
export function parseIsoDay(value: unknown): string | undefined {
  return typeof value === 'string' && isDateOnly(value) ? value : undefined;
}
